import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { PopoverStateChangedEffect } from '../../PopoverStateChangedEffect';
import { Pill } from '../nucleus/Pill/Pill';
import { SortedBy } from './sortJobs';

export { SortedBy, sortJobs } from './sortJobs';

type Option = { key: string; name: string };

export function SortBy({
  className,
  options,
  value,
  onChange,
}: {
  className: string;
  options: Option[];
  value: SortedBy;
  onChange: (value: SortedBy) => void;
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(() => value);

  // Update internal state when prop value changes
  useEffect(() => {
    setSelected(value);
  }, [value]);
  const { isMobile } = useBreakpoint();

  return (
    <Popover className={classNames('relative', className)}>
      {({ open, close }) => {
        const getLabel = () => {
          const selectedOption = options.find((opt) => opt.key === selected);
          return `${t('sortBy')}: ${selectedOption?.name}`;
        };

        return (
          <>
            <PopoverButton
              className={classNames(
                isMobile
                  ? 'mx-1 my-1 rounded-full'
                  : 'label flex h-8 w-fit cursor-pointer items-center rounded-sm bg-white bg-opacity-20 px-4 text-base lg:h-auto lg:bg-opacity-100 lg:px-2 lg:py-1',
                className
              )}
            >
              {isMobile ? (
                <Pill
                  type='filter'
                  hasParent
                  active={true}
                  icon={{ position: 'right', icon: faChevronDown }}
                >
                  {getLabel()}
                </Pill>
              ) : (
                <>
                  <span className='mr-1 w-auto whitespace-nowrap text-white lg:mx-1 lg:text-secondary'>
                    {t('sortBy')}:
                  </span>
                  <span className='cursor-pointer font-semibold text-white lg:font-medium lg:text-primary'>
                    {options.find((opt) => opt.key === selected)?.name}
                  </span>
                  <FontAwesomeIcon
                    className='ml-1.5 h-3 w-3'
                    // @ts-ignore
                    icon={faChevronDown}
                  />
                </>
              )}
            </PopoverButton>

            <PopoverPanel
              anchor='bottom end'
              className='prevent-drag-scroll absolute z-[100] w-48 rounded border border-outline-variant bg-white shadow-lg'
              style={{ marginTop: '8px' }}
            >
              <div className='py-1'>
                {options.map(({ key, name }) => (
                  <button
                    key={key}
                    className={classNames(
                      'block w-full px-4 py-2 text-left text-secondary hover:bg-grey-5 hover:text-primary',
                      { 'bg-grey-5 text-primary': key === selected }
                    )}
                    onClick={() => {
                      onChange(key as SortedBy);
                      setSelected(key as SortedBy);
                      close();
                    }}
                  >
                    {name}
                  </button>
                ))}
              </div>
            </PopoverPanel>

            <PopoverStateChangedEffect open={open} onClose={() => {}} />
          </>
        );
      }}
    </Popover>
  );
}
